import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Eye, FileText, MagnifyingGlass, ArrowClockwise, Download, Fire, Pause, FileXls } from '@phosphor-icons/react';
import { formatAnyDate } from '../../utils/dateFormat';

const STATUS_OPTIONS = [
  'pending_procurement', 'procurement_approved', 'procurement_rejected',
  'assigned_to_production', 'under_production', 'production_on_hold',
  'production_completed', 'ready_to_dispatch', 'scheduled_for_dispatch',
  'dispatched', 'partially_dispatched', 'pending_install_requests',
  'pending_for_installation', 'installation_completed', 'cancelled',
];

const fmtINR = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

function MobileJobOrders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    executive_name: '', customer_name: '', customer_mobile: '',
    jo_number: '', quotation_no: '', jo_date_from: '', jo_date_to: '',
    status: '', include_archived: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      Object.keys(params).forEach((k) => { if (params[k] === '' || params[k] === false) delete params[k]; });
      const { data } = await api.get('/api/admin/mobile-job-orders', { params });
      setRows(data.job_orders || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load job orders');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const openPdf = (jo) => {
    window.open(`/api/admin/mobile-job-orders/${encodeURIComponent(jo)}/pdf`, '_blank');
  };

  const exportExcel = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v !== false) params.set(k, v); });
    window.open(`/api/admin/mobile-job-orders/export?${params.toString()}`, '_blank');
  };

  return (
    <Layout>
      <div className="space-y-6 pb-12">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-light tracking-tight text-zinc-950">Mobile Job Orders</h1>
            <p className="text-sm text-zinc-500 mt-1">Read-only view of job orders synced from the mobile app.</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="export-excel-btn" variant="outline" className="border-emerald-400 text-emerald-700 hover:bg-emerald-50" onClick={exportExcel}>
              <FileXls size={16} className="mr-1" /> Export Excel
            </Button>
            <Button data-testid="refresh-btn" variant="outline" onClick={load}>
              <ArrowClockwise size={16} className="mr-1" /> Refresh
            </Button>
          </div>
        </div>

        <Card className="border-zinc-300 rounded-sm">
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <Input data-testid="filter-jo-number" placeholder="JO #" value={filters.jo_number} onChange={(e) => setFilters({ ...filters, jo_number: e.target.value })} />
            <Input data-testid="filter-quotation-no" placeholder="Quotation #" value={filters.quotation_no} onChange={(e) => setFilters({ ...filters, quotation_no: e.target.value })} />
            <Input data-testid="filter-customer-name" placeholder="Customer Name" value={filters.customer_name} onChange={(e) => setFilters({ ...filters, customer_name: e.target.value })} />
            <Input data-testid="filter-customer-mobile" placeholder="Customer Mobile" value={filters.customer_mobile} onChange={(e) => setFilters({ ...filters, customer_mobile: e.target.value })} />
            <Input data-testid="filter-executive" placeholder="Executive" value={filters.executive_name} onChange={(e) => setFilters({ ...filters, executive_name: e.target.value })} />
            <select
              data-testid="filter-status"
              className="h-10 px-3 border border-zinc-300 rounded-sm bg-white text-sm"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <Input data-testid="filter-date-from" type="date" value={filters.jo_date_from} onChange={(e) => setFilters({ ...filters, jo_date_from: e.target.value })} />
            <Input data-testid="filter-date-to" type="date" value={filters.jo_date_to} onChange={(e) => setFilters({ ...filters, jo_date_to: e.target.value })} />
            <label className="flex items-center gap-2 text-sm">
              <input
                data-testid="filter-include-archived"
                type="checkbox"
                checked={filters.include_archived}
                onChange={(e) => setFilters({ ...filters, include_archived: e.target.checked })}
              />
              Include archived
            </label>
            <Button data-testid="apply-filter-btn" onClick={load} className="bg-orange-500 hover:bg-orange-600 text-white">
              <MagnifyingGlass size={16} className="mr-1" /> Apply
            </Button>
            <Button
              variant="outline"
              data-testid="clear-filter-btn"
              onClick={() => setFilters({ executive_name: '', customer_name: '', customer_mobile: '', jo_number: '', quotation_no: '', jo_date_from: '', jo_date_to: '', status: '', include_archived: false })}
            >
              Clear
            </Button>
          </CardContent>
        </Card>

        <Card className="border-zinc-300 rounded-sm">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-300">
                <tr>
                  <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">JO #</th>
                  <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Date</th>
                  <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Customer</th>
                  <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Mobile</th>
                  <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Executive</th>
                  <th className="text-center p-3 font-bold uppercase text-xs text-zinc-700">Status</th>
                  <th className="text-center p-3 font-bold uppercase text-xs text-zinc-700">Flags</th>
                  <th className="text-center p-3 font-bold uppercase text-xs text-zinc-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="p-6 text-center text-zinc-400">Loading...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={8} className="p-6 text-center text-zinc-400">No job orders found</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.jo_number} className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`jo-row-${r.jo_number}`}>
                    <td className="p-3 font-mono">
                      <Link to={`/admin/mobile-job-orders/${encodeURIComponent(r.jo_number)}`} className="text-blue-600 hover:underline">
                        {r.jo_number}
                      </Link>
                      <Badge
                        data-testid={`mobile-jo-badge-${r.jo_number}`}
                        className="ml-2 bg-blue-100 text-blue-700 hover:bg-blue-100 text-[9px]"
                      >Mobile JO</Badge>
                    </td>
                    <td className="p-3 text-xs">{formatAnyDate(r.jo_date) || '—'}</td>
                    <td className="p-3">{r.customer_name || '—'}</td>
                    <td className="p-3 font-mono text-xs">{r.customer_mobile || '—'}</td>
                    <td className="p-3 text-xs">{r.executive_name || '—'}</td>
                    <td className="p-3 text-center">
                      <Badge variant="outline" className="text-[10px] uppercase">{r.status || '—'}</Badge>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex gap-1 justify-center">
                        {r.overlay?.is_urgent && (
                          <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[10px]"><Fire size={12} className="mr-1" />URGENT</Badge>
                        )}
                        {r.overlay?.on_hold && (
                          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]"><Pause size={12} className="mr-1" />HOLD</Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex gap-1 justify-center">
                        <Link to={`/admin/mobile-job-orders/${encodeURIComponent(r.jo_number)}`}>
                          <Button data-testid={`view-${r.jo_number}`} variant="ghost" size="sm"><Eye size={16} /></Button>
                        </Link>
                        <Button data-testid={`pdf-${r.jo_number}`} variant="ghost" size="sm" onClick={() => openPdf(r.jo_number)}><FileText size={16} /></Button>
                        <a href={`/api/admin/mobile-job-orders/${encodeURIComponent(r.jo_number)}/pdf`} download>
                          <Button variant="ghost" size="sm"><Download size={16} /></Button>
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

export default MobileJobOrders;
export { MobileJobOrders };
